import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { requestApi } from 'api/Api';
import { createQuote } from 'api/Quotes';

import { io, reShapeProcedures } from 'lib/helpers/utility';
import { usePackages } from 'lib/hooks/queries/packages/usePackages';
import { useProducts } from 'lib/hooks/queries/products/useProducts';
import { useServices } from 'lib/hooks/queries/services/useServices';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { showAlert } from 'components/shared/Alert/Alert';
import Modal from 'components/shared/Modal/Modal';

import Footer from './components/Footer';
import Header from './components/Header';
import QuoteContent from './components/QuoteContent';
import {
  extractCheckedProcedures,
  extractCheckedProducts,
  extractProceduresFromPackages,
  extractProductsFromPackages
} from './lib/helpers';

const QuoteValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  patient: Yup.object().required('Patient is required.')
});

const QuoteModal = ({ showQuoteModal, setShowQuoteModal }) => {
  const ref = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [firstMount, setFirstMount] = useState(true);
  const [mode, setMode] = useState({ readonly: false, created: false, converted: false });

  const { mutate: mutateCreateQuote, isLoading } = useCustomMutation({
    invalidateQueryKey: ['quotes'],
    successMessage: 'Quote created successfully.',
    errorMessage: 'Failed to create Quote!',
    mutationFn: (params) => createQuote(navigate, params),
    onSuccess: (data) => {
      if (!data?.invoice) {
        return;
      }

      setMode((prev) => ({ ...prev, created: true }));

      const newInvoice = {
        ...data?.invoice,
        selected: true,
        expanded: true,
        procedures: JSON.parse(data?.invoice?.procedures),
        packages: JSON.parse(data?.invoice?.packages),
        products: JSON.parse(data?.invoice?.products),
        discount: JSON.parse(data?.invoice?.discount)
      };

      setInvoice(newInvoice);
    }
  });

  const finalLoading = isLoading ?? loading;

  const formik = useFormik({
    initialValues: { patient: null, name: '', services: [], packages: [], products: [] },
    validationSchema: QuoteValidationSchema,
    onSubmit: () => {}
  });

  useEffect(() => {
    setFirstMount(false);
  }, []);

  useEffect(() => {
    if (
      !firstMount &&
      (location.pathname === '/portal/settings/packages' ||
        location.pathname === '/portal/settings/products')
    ) {
      setShowQuoteModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const hideQuoteModal = () => setShowQuoteModal(false);

  const checkQuote = () => {
    const { values, errors, submitForm } = formik;

    setLoading(true);
    submitForm();

    if (io(errors)) {
      setLoading(false);
      return;
    }

    const onSuccess = (data) => {
      if (!!data?.invoice) {
        const newInvoice = { ...data?.invoice, selected: true, expanded: true };
        setInvoice(newInvoice);
        setMode((prev) => ({ ...prev, readonly: true }));
      }
      setLoading(false);
    };

    const onError = (error, code) => {
      if (code == 4) {
        error.map((errorMessage) =>
          showAlert({ title: 'Error', message: errorMessage, color: 'danger' })
        );
      }
      setLoading(false);
    };

    const productsFromPackToBeChecked = extractProductsFromPackages(values?.packages || []);

    const productsToBeChecked = extractCheckedProducts(values?.products || []);

    const productsId = [...productsToBeChecked, ...productsFromPackToBeChecked];

    const proceduresToBeChecked = extractCheckedProcedures(values?.services || []);

    const proceduresFromPack = extractProceduresFromPackages(values?.packages || []);

    const servicesId = [...proceduresToBeChecked, ...proceduresFromPack];

    const invoice = {
      discount: {},
      expanded: true,
      selected: true,
      name: formik?.values?.name,
      services: formik?.values?.services,
      packages: formik?.values?.packages,
      products: formik?.values?.products,
      patient_id: formik?.values?.patient?.value
    };

    requestApi({
      url: '/api/patient/quote/check',
      params: {
        invoice,
        productsId,
        servicesId
      },
      onSuccess,
      navigate,
      onError
    });
  };

  const createQuoteHandler = () => {
    const { selected: _, expanded: __, ...restInvoice } = invoice;

    mutateCreateQuote({ invoice: restInvoice });
  };

  const convertQuote = () => {
    const onSuccess = (data) => {
      if (!!data?.invoice) {
        setMode((prev) => ({ ...prev, converted: true }));
        showAlert({ title: 'Quote', message: 'Quote converted to invoice.' });
      }
      setLoading(false);
    };

    const onError = (error, code) => {
      if (code == 4) {
        error.map((errorMessage) =>
          showAlert({ title: 'Error', message: errorMessage, color: 'danger' })
        );
      }
      setLoading(false);
    };

    setLoading(true);

    requestApi({
      onError,
      navigate,
      onSuccess,
      params: { invoice },
      url: '/api/patient/quote/convert'
    });
  };

  const { data: packagesData } = usePackages({
    params: { forUsers: true }
  });

  const mappedPackages = useMemo(() => {
    return (
      packagesData?.packages?.map((item) => ({
        ...item,
        sales_count: 1,
        total_amount_cents: item.amount_cents
      })) || []
    );
  }, [packagesData?.packages]);

  const { data: productsData } = useProducts({
    params: { forUsers: true }
  });

  const mappedProducts = useMemo(() => {
    return (
      productsData?.products?.map((prod) => ({
        ...prod,
        sales_count: 1,
        total_amount_cents: prod?.amount_cents
      })) || []
    );
  }, [productsData?.products]);

  const { data: servicesData } = useServices({
    params: {
      offset: 0,
      limit: null
    }
  });

  const mappedServices = useMemo(() => {
    return reShapeProcedures(servicesData?.services || []);
  }, [servicesData?.services]);

  return (
    <Modal
      slideFromRight
      className="!w-[800px] bg-white"
      isOpen={showQuoteModal}
      title="Create a new quote"
      handleClose={hideQuoteModal}
      headButton={
        <Header mode={mode} ref={ref} finalLoading={finalLoading} convertQuote={convertQuote} />
      }
      bodyClassName={`!max-h-[unset] !h-full ${mode?.created ? '!p-0 ' : '!p-4'}`}
      footer={
        <Footer
          mode={mode}
          setMode={setMode}
          finalLoading={finalLoading}
          formik={formik}
          hideQuoteModal={hideQuoteModal}
          checkQuote={checkQuote}
          createQuoteHandler={createQuoteHandler}
        />
      }>
      <QuoteContent
        ref={ref}
        mode={mode}
        invoice={invoice}
        setInvoice={setInvoice}
        formik={formik}
        services={mappedServices}
        products={mappedProducts}
        packages={mappedPackages}
      />
    </Modal>
  );
};

export default QuoteModal;
